import { useCallback, useEffect, useRef, useState } from 'react';
import {
  REVIEW_CHECKS,
  type ContinuityProfileDto,
  type ProjectCosts,
  type ProjectSettings,
  type ReviewCheck,
  type ShotDto,
  type ShotEstimate,
  type ShotTakeDto,
} from '@gc/shared';
import { ApiError } from '../../api/client.js';
import {
  actualizarPlano,
  elegirTake,
  eliminarPlano,
  estimarPlano,
  generarPlano,
  generarStoryboard,
  guardarPerfil,
  listarPlanos,
  obtenerCostes,
  obtenerPerfil,
  revisarTake,
} from '../../api/advancedVideo.js';
import { SelectorAsset } from '../biblioteca/SelectorAsset.js';

export type PanelVideoAvanzadoProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly settings: ProjectSettings;
  onCambio(): void;
};

const ETIQUETA_CHECK: Record<ReviewCheck, string> = {
  identity: 'Identidad del sujeto',
  wardrobe: 'Vestuario',
  location: 'Localización',
  lighting: 'Luz y estilo',
  motion: 'Dirección del movimiento',
  product: 'Producto',
  quality: 'Calidad técnica',
};

const ESTADO_PLANO: Record<string, string> = {
  DRAFT: 'Sin clip',
  GENERATING: 'Generando…',
  NEEDS_REVIEW: 'Por revisar',
  REVIEWED: 'Revisado',
};

const usd = (v: string) => `${Number(v).toFixed(2)} $`;

/**
 * Fase 09 en la página del proyecto: storyboard → referencias → planos con
 * estimación y generación → revisión humana de cada take → take activo.
 * Todo lo que gasta muestra el coste y el presupuesto antes, y nada llega al
 * montaje sin una revisión PASS y una selección explícita.
 */
export function PanelVideoAvanzado({ projectId, revision, status, settings, onCambio }: PanelVideoAvanzadoProps): React.JSX.Element {
  const [planos, setPlanos] = useState<ShotDto[] | null>(null);
  const [perfil, setPerfil] = useState<ContinuityProfileDto | null>(null);
  const [costes, setCostes] = useState<ProjectCosts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const avisar = useRef(onCambio);
  avisar.current = onCambio;

  const recargar = useCallback(async () => {
    try {
      const [p, pf, c] = await Promise.all([listarPlanos(projectId), obtenerPerfil(projectId), obtenerCostes(projectId)]);
      setPlanos(p);
      setPerfil(pf);
      setCostes(c);
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo cargar el vídeo avanzado.');
    }
  }, [projectId]);

  useEffect(() => {
    void recargar();
  }, [recargar, revision, status]);

  // Mientras algo se genera (storyboard o clips) se refresca; al terminar, se avisa a la página.
  const generando = status === 'GENERATING' || (planos?.some((p) => p.status === 'GENERATING') ?? false);
  useEffect(() => {
    if (!generando) return;
    const intervalo = setInterval(() => {
      void recargar();
      // El estado del proyecto lo tiene la página: mientras genera, se le pide
      // que lo refresque para que el panel salga de «generando» al terminar.
      if (status === 'GENERATING') avisar.current();
    }, 4_000);
    return () => clearInterval(intervalo);
  }, [generando, recargar, status]);
  const generandoAntes = useRef(generando);
  useEffect(() => {
    if (generandoAntes.current && !generando) avisar.current();
    generandoAntes.current = generando;
  }, [generando]);

  async function accion(fn: () => Promise<unknown>, recargarPagina = true): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await fn();
      await recargar();
      if (recargarPagina) avisar.current();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'La operación falló.');
    } finally {
      setOcupado(false);
    }
  }

  const bloqueado = ocupado || status === 'RENDERING' || status === 'GENERATING';
  const sinPlanos = planos !== null && planos.length === 0;

  return (
    <section aria-labelledby="titulo-avanzado" style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}>
      <h2 id="titulo-avanzado" style={{ marginTop: 0 }}>Vídeo avanzado</h2>

      {/* --- Presupuesto --- */}
      {costes !== null && (
        <p>
          {costes.policies.map((p) => (
            <span key={p.scope} style={{ marginRight: '1.25rem' }}>
              <strong>{p.scope === 'PROJECT' ? 'Tope del proyecto' : 'Tope mensual'}:</strong> {usd(p.committedUsd)} gastado
              {Number(p.reservedUsd) > 0 ? ` + ${usd(p.reservedUsd)} reservado` : ''} de {usd(p.limitUsd)} · quedan {usd(p.availableUsd)}
              {p.unknownCount > 0 ? ` · ${p.unknownCount} con coste desconocido` : ''}
            </span>
          ))}
          <br />
          <small>Importes con la tarifa publicada del proveedor (USD). El proveedor no informa del coste real.</small>
        </p>
      )}

      {/* --- Storyboard --- */}
      <p>
        <button
          type="button"
          disabled={bloqueado}
          onClick={() => void accion(() => generarStoryboard(projectId, revision))}
        >
          {sinPlanos ? 'Generar storyboard' : 'Volver a generar el storyboard'}
        </button>{' '}
        <small>
          {sinPlanos
            ? 'Crea de 2 a 8 planos a partir de la idea (una llamada de texto).'
            : 'Sustituye los planos actuales; los clips ya generados quedan en la biblioteca.'}
        </small>
      </p>
      {status === 'GENERATING' && <p role="status">Generando el storyboard…</p>}

      {/* --- Continuidad --- */}
      {planos !== null && planos.length > 0 && (
        <Continuidad projectId={projectId} revision={revision} perfil={perfil} bloqueado={bloqueado} onGuardar={(input) => void accion(() => guardarPerfil(projectId, input))} />
      )}

      {/* --- Planos --- */}
      {planos !== null && planos.length > 0 && (
        <ol style={{ paddingLeft: '1.25rem' }}>
          {planos.map((plano) => (
            <li key={plano.id} style={{ margin: '1rem 0' }}>
              <Plano
                projectId={projectId}
                revision={revision}
                plano={plano}
                settings={settings}
                bloqueado={bloqueado}
                tieneReferencias={(perfil?.references.length ?? 0) > 0}
                onAccion={accion}
              />
            </li>
          ))}
        </ol>
      )}

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}

// --- Continuidad ---------------------------------------------------------------

function Continuidad({ projectId, revision, perfil, bloqueado, onGuardar }: {
  projectId: string; revision: number; perfil: ContinuityProfileDto | null; bloqueado: boolean;
  onGuardar(input: { expectedRevision: number; strategy: 'REFERENCES'; lockedTraits: string[]; references: { assetId: string; role: 'subject' }[] }): void;
}): React.JSX.Element {
  const [rasgos, setRasgos] = useState((perfil?.lockedTraits ?? []).join('\n'));
  const [referencias, setReferencias] = useState<string[]>(perfil?.references.map((r) => r.assetId) ?? []);
  useEffect(() => {
    setRasgos((perfil?.lockedTraits ?? []).join('\n'));
    setReferencias(perfil?.references.map((r) => r.assetId) ?? []);
  }, [perfil]);

  const cambiado = rasgos !== (perfil?.lockedTraits ?? []).join('\n') || referencias.join() !== (perfil?.references.map((r) => r.assetId) ?? []).join();

  return (
    <details open={perfil === null || perfil.references.length === 0} style={{ border: '1px solid var(--borde-suave)', borderRadius: '6px', padding: '0.75rem 1rem', margin: '1rem 0' }}>
      <summary>
        <strong>Continuidad</strong> — {perfil === null ? 'sin perfil' : `revisión ${perfil.revision}, ${perfil.references.length} referencia(s)`}
      </summary>
      <p>
        <small>
          Hasta 3 imágenes de referencia del sujeto (persona, personaje o producto) que el proveedor usa en todos los planos.
          Con referencias los clips son siempre de 8 s. La continuidad se revisa a mano en cada take.
        </small>
      </p>
      <p>
        <label htmlFor="rasgos-bloqueados">Rasgos invariantes (uno por línea, en inglés)</label>
        <br />
        <textarea id="rasgos-bloqueados" rows={3} value={rasgos} disabled={bloqueado} onChange={(e) => setRasgos(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
      </p>
      {[0, 1, 2].map((indice) => (
        <div key={indice} style={{ margin: '0.5rem 0' }}>
          <small>Referencia {indice + 1}</small>
          <SelectorAsset
            projectId={projectId}
            usage="LIBRARY"
            tiposPermitidos={['IMAGE']}
            seleccionado={referencias[indice] ?? null}
            deshabilitado={bloqueado}
            {...(indice === referencias.length ? { etiquetaSubida: 'Subir una imagen de referencia' } : {})}
            onSeleccionar={(assetId) => {
              const siguiente = [...referencias];
              if (assetId === null) siguiente.splice(indice, 1);
              else siguiente[indice] = assetId;
              setReferencias(siguiente.filter((v, i, a) => v !== undefined && a.indexOf(v) === i));
            }}
          />
        </div>
      ))}
      <p>
        <button
          type="button"
          disabled={bloqueado || !cambiado || rasgos.trim() === ''}
          onClick={() =>
            onGuardar({
              expectedRevision: revision,
              strategy: 'REFERENCES',
              lockedTraits: rasgos.split('\n').map((l) => l.trim()).filter((l) => l !== ''),
              references: referencias.map((assetId) => ({ assetId, role: 'subject' as const })),
            })
          }
        >
          Guardar continuidad
        </button>{' '}
        <small>Guardar sube la revisión del proyecto; los takes ya generados se conservan.</small>
      </p>
    </details>
  );
}

// --- Plano ---------------------------------------------------------------------

function Plano({ projectId, revision, plano, settings, bloqueado, tieneReferencias, onAccion }: {
  projectId: string; revision: number; plano: ShotDto; settings: ProjectSettings; bloqueado: boolean; tieneReferencias: boolean;
  onAccion(fn: () => Promise<unknown>, recargarPagina?: boolean): Promise<void>;
}): React.JSX.Element {
  const [editando, setEditando] = useState(false);
  const [accion, setAccion] = useState(plano.action);
  const [camara, setCamara] = useState(plano.camera);
  const [entorno, setEntorno] = useState(plano.environment);
  const [estimacion, setEstimacion] = useState<ShotEstimate | null>(null);
  const [resolucion, setResolucion] = useState<'720p' | '1080p'>('720p');

  useEffect(() => {
    setAccion(plano.action);
    setCamara(plano.camera);
    setEntorno(plano.environment);
  }, [plano.action, plano.camera, plano.environment]);

  async function estimar(): Promise<void> {
    try {
      setEstimacion(await estimarPlano(projectId, plano.id, { resolution: resolucion }));
    } catch (causa) {
      setEstimacion(null);
      throw causa;
    }
  }

  const activo = plano.takes.find((t) => t.id === plano.activeTakeId) ?? null;

  return (
    <article style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '0.75rem 1rem' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'baseline', border: 0, paddingBottom: 0 }}>
        <strong>
          {plano.position + 1}. {plano.title}
        </strong>
        <span>
          <small>{ESTADO_PLANO[plano.status] ?? plano.status} · clip de {plano.providerDurationMs / 1000} s</small>
        </span>
      </header>
      <p style={{ margin: '0.25rem 0', color: 'var(--texto-suave)' }}>{plano.description}</p>

      {!editando ? (
        <p style={{ margin: '0.25rem 0' }}>
          <small>
            <strong>Acción:</strong> {plano.action} · <strong>Cámara:</strong> {plano.camera} · <strong>Entorno:</strong> {plano.environment}
          </small>{' '}
          <button type="button" disabled={bloqueado} onClick={() => setEditando(true)}>Editar</button>{' '}
          <button
            type="button"
            disabled={bloqueado}
            onClick={() => {
              if (window.confirm('¿Quitar este plano del montaje? Los clips generados quedan en la biblioteca.')) {
                void onAccion(() => eliminarPlano(projectId, plano.id, revision));
              }
            }}
          >
            Quitar
          </button>
        </p>
      ) : (
        <div style={{ margin: '0.5rem 0' }}>
          <label>Acción (inglés)<br /><input value={accion} onChange={(e) => setAccion(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} /></label>
          <label>Cámara (inglés)<br /><input value={camara} onChange={(e) => setCamara(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} /></label>
          <label>Entorno (inglés)<br /><input value={entorno} onChange={(e) => setEntorno(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} /></label>
          <p>
            <button
              type="button"
              disabled={bloqueado}
              onClick={() =>
                void onAccion(() => actualizarPlano(projectId, plano.id, { expectedRevision: revision, action: accion, camera: camara, environment: entorno })).then(() => setEditando(false))
              }
            >
              Guardar plano
            </button>{' '}
            <button type="button" onClick={() => setEditando(false)}>Cancelar</button>
          </p>
        </div>
      )}

      {/* Estimar y generar */}
      <p style={{ margin: '0.5rem 0' }}>
        <label>
          Resolución{' '}
          <select value={resolucion} disabled={bloqueado} onChange={(e) => { setResolucion(e.target.value as '720p' | '1080p'); setEstimacion(null); }}>
            <option value="720p">720p</option>
            <option value="1080p">1080p (solo clips de 8 s)</option>
          </select>
        </label>{' '}
        <button type="button" disabled={bloqueado} onClick={() => void onAccion(estimar, false)}>
          Estimar coste
        </button>{' '}
        {estimacion !== null && (
          <>
            <span>
              {usd(estimacion.estimatedCostUsd)} ({estimacion.durationMs / 1000} s × {estimacion.pricePerSecondUsd} $/s, {estimacion.model}
              {tieneReferencias ? `, ${estimacion.references.length} referencia(s)` : ', sin referencias'})
            </span>{' '}
            <button
              type="button"
              disabled={bloqueado || !estimacion.allowed}
              title={estimacion.reason ?? 'Reserva el coste y envía la generación'}
              onClick={() => void onAccion(() => generarPlano(projectId, plano.id, { expectedRevision: revision, resolution: resolucion }))}
            >
              {plano.takes.length === 0 ? 'Generar clip' : 'Generar otro take'}
            </button>
            {!estimacion.allowed && <small role="alert"> {estimacion.reason}</small>}
            <br />
            <small>
              Takes: {estimacion.takesSoFar}/{estimacion.takesWithoutApproval} sin nueva aprobación · tarifa comprobada el {estimacion.priceSource.checkedAt}
            </small>
          </>
        )}
      </p>

      {/* Takes */}
      {plano.takes.length > 0 && (
        <div>
          <strong>Takes</strong>
          {plano.takes.map((take) => (
            <Take key={take.id} projectId={projectId} revision={revision} shotId={plano.id} take={take} esActivo={activo?.id === take.id} bloqueado={bloqueado} aspectRatio={settings.aspectRatio} onAccion={onAccion} />
          ))}
        </div>
      )}
    </article>
  );
}

// --- Take ----------------------------------------------------------------------

function Take({ projectId, revision, shotId, take, esActivo, bloqueado, aspectRatio, onAccion }: {
  projectId: string; revision: number; shotId: string; take: ShotTakeDto; esActivo: boolean; bloqueado: boolean; aspectRatio: '16:9' | '9:16';
  onAccion(fn: () => Promise<unknown>, recargarPagina?: boolean): Promise<void>;
}): React.JSX.Element {
  const [checks, setChecks] = useState<Record<ReviewCheck, 'PASS' | 'REJECT' | 'NOT_APPLICABLE'>>(
    Object.fromEntries(REVIEW_CHECKS.map((c) => [c, c === 'product' ? 'NOT_APPLICABLE' : 'PASS'])) as Record<ReviewCheck, 'PASS' | 'REJECT' | 'NOT_APPLICABLE'>,
  );
  const [notas, setNotas] = useState('');
  const ultima = take.reviews[0] ?? null;
  const aprobado = ultima?.decision === 'PASS';
  const algunRechazo = Object.values(checks).includes('REJECT');

  return (
    <div style={{ border: '1px solid var(--borde-suave)', borderRadius: '6px', padding: '0.5rem 0.75rem', margin: '0.5rem 0', background: esActivo ? 'var(--acento-fondo)' : undefined }}>
      <p style={{ margin: '0 0 0.25rem' }}>
        <small>
          {new Date(take.createdAt).toLocaleString('es-ES')} · {take.status} · {take.inputSnapshot.model} · {take.inputSnapshot.durationMs / 1000} s · {usd(take.inputSnapshot.estimatedCostUsd)}
          {esActivo ? ' · en el montaje' : ''}
          {ultima ? ` · revisión: ${ultima.decision}` : ''}
        </small>
      </p>
      {take.status === 'FAILED' && (
        <p role="alert">Falló: {(take.technicalError as { message?: string } | null)?.message ?? 'sin detalle'}</p>
      )}
      {take.status === 'RUNNING' || take.status === 'PENDING' ? <p role="status">Generando… el proveedor tarda entre 11 s y 6 min.</p> : null}
      {take.contentUrl !== null && (
        <video src={take.contentUrl} controls preload="metadata" style={{ width: '100%', maxWidth: aspectRatio === '16:9' ? '28rem' : '14rem' }} />
      )}

      {take.status === 'COMPLETED' && !aprobado && (
        <div style={{ marginTop: '0.5rem' }}>
          <small><strong>Revisión de continuidad</strong></small>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.25rem 1rem' }}>
            {REVIEW_CHECKS.map((c) => (
              <label key={c} style={{ fontSize: '0.85rem' }}>
                {ETIQUETA_CHECK[c]}{' '}
                <select value={checks[c]} disabled={bloqueado} onChange={(e) => setChecks({ ...checks, [c]: e.target.value as 'PASS' | 'REJECT' | 'NOT_APPLICABLE' })}>
                  <option value="PASS">Bien</option>
                  <option value="REJECT">Rechazar</option>
                  <option value="NOT_APPLICABLE">No aplica</option>
                </select>
              </label>
            ))}
          </div>
          <p style={{ margin: '0.25rem 0' }}>
            <input placeholder="Notas de la revisión" value={notas} disabled={bloqueado} onChange={(e) => setNotas(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
          </p>
          <p style={{ margin: '0.25rem 0' }}>
            <button type="button" disabled={bloqueado || algunRechazo} onClick={() => void onAccion(() => revisarTake(projectId, shotId, take.id, { checks, decision: 'PASS', notes: notas }), false)}>
              Aprobar take
            </button>{' '}
            <button type="button" disabled={bloqueado} onClick={() => void onAccion(() => revisarTake(projectId, shotId, take.id, { checks, decision: 'REJECT', notes: notas }), false)}>
              Rechazar take
            </button>
            {algunRechazo && <small> Con un criterio rechazado solo cabe rechazar el take.</small>}
          </p>
        </div>
      )}

      {take.status === 'COMPLETED' && aprobado && !esActivo && (
        <p style={{ margin: '0.25rem 0' }}>
          <button type="button" disabled={bloqueado} onClick={() => void onAccion(() => elegirTake(projectId, shotId, take.id, revision))}>
            Usar este take en el montaje
          </button>{' '}
          <small>Sustituye el visual de la escena y sube la revisión del proyecto.</small>
        </p>
      )}
      {ultima?.notes ? <p style={{ margin: 0 }}><small>Notas: {ultima.notes}</small></p> : null}
    </div>
  );
}
